import { Registry } from '../core/Registry.js';
import { EV } from '../core/EventBus.js';
import { setGoalUnlocked } from '../levels/LevelBuilder.js';
import { redlineMedal } from './Redline.js';

export const gameModes = new Registry('gameModes');

/**
 * Rules layer. A mode owns the win/lose condition, the objective line in the
 * HUD and the scoring. Levels pick one by key, so a new mode is a new file
 * plus one registry call.
 */
export class GameMode {
  constructor(level, ctx) {
    this.level = level;
    this.ctx = ctx;
    this.config = level.def.mode ?? {};
    this.time = 0;
    this.finished = false;
    this._unsub = [];
  }

  start() {}
  update(dt) {
    if (this.finished) return;
    this.time += dt;
    // REDLINE: a hard limit on any mode (docs/REDLINE.md). Crossing it
    // ends the run, never the life: the dead screen offers a restart.
    if (this.config.redline && this.time >= this.config.redline) this.fail('OVER THE REDLINE');
  }

  /** Seconds left on a redline clock, or null when there is none. */
  get redlineLeft() {
    return this.config.redline ? Math.max(0, this.config.redline - this.time) : null;
  }

  /** @returns {{objective:string, detail?:string, danger?:boolean}} */
  hud() { return { objective: '' }; }

  /** Called when the player enters an unlocked goal. */
  onGoal() { this.complete(); }

  complete() {
    if (this.finished) return;
    this.finished = true;
    this.ctx.bus.emit(EV.LEVEL_COMPLETE, { mode: this, time: this.time });
  }

  fail(reason) {
    if (this.finished) return;
    this.finished = true;
    this.ctx.bus.emit(EV.LEVEL_FAILED, { mode: this, reason });
  }

  listen(type, fn) { this._unsub.push(this.ctx.bus.on(type, fn)); }

  dispose() { for (const off of this._unsub) off(); this._unsub.length = 0; }

  /** Medal thresholds derived from par: gold at par, silver at 1.35x, bronze at 1.8x. */
  medal() {
    if (this.config.redline) return redlineMedal(this.config.redline, this.time);
    const par = this.config.par;
    if (!par) return 'none';
    if (this.time <= par) return 'gold';
    if (this.time <= par * 1.35) return 'silver';
    if (this.time <= par * 1.8) return 'bronze';
    return 'none';
  }
}

/** Reach the exit. Time is the score. */
class SprintMode extends GameMode {
  start() { setGoalUnlocked(this.level, true); }
  hud() {
    return { objective: this.config.objective ?? 'REACH THE EXIT GATE' };
  }
}

/** Collect every data shard, then the gate opens. */
class CollectMode extends GameMode {
  start() {
    this.total = this.level.shardTotal;
    this.got = 0;
    setGoalUnlocked(this.level, this.total === 0);
    this.listen(EV.PICKUP, (p) => {
      if (p.kind !== 'shard' || this.finished) return;
      this.got++;
      if (this.got >= this.total) {
        setGoalUnlocked(this.level, true);
        this.ctx.bus.emit(EV.TOAST, { text: 'GATE UNLOCKED\nGET OUT' });
      } else {
        this.ctx.bus.emit(EV.TOAST, { text: `SHARD ${this.got}/${this.total}` });
      }
    });
  }
  hud() {
    return {
      objective: this.got >= this.total ? 'REACH THE EXIT GATE' : 'RECOVER THE DATA SHARDS',
      shards: { got: this.got, total: this.total },
    };
  }
}

/** Reach the exit before the clock runs out. */
class GauntletMode extends GameMode {
  start() {
    setGoalUnlocked(this.level, true);
    this.limit = this.config.limit ?? 60;
  }
  update(dt) {
    super.update(dt);
    if (this.finished) return;
    if (this.time >= this.limit) this.fail('The lockdown timer beat you.');
  }
  get remaining() { return Math.max(0, this.limit - this.time); }
  hud() {
    const r = this.remaining;
    return {
      objective: 'OUTRUN THE LOCKDOWN',
      countdown: r,
      danger: r < 10,
    };
  }
}

/** One life, depth as the score. The tower owns its own death planes. */
class TowerMode extends GameMode {
  start() {
    this.depth = 1;
    this.planes = this.level.def.gauntlet?.planes ?? [];
    this.deathPlanes = this.level.def.gauntlet?.deathPlanes ?? [];
  }
  update(dt) {
    super.update(dt);
    if (this.finished) return;
    const p = this.ctx.player?.position;
    if (!p) return;
    while (this.depth < this.planes.length && p.z < this.planes[this.depth]) this.depth++;
    const floor = this.deathPlanes[Math.max(0, this.depth - 1)] ?? -1000;
    if (p.y < floor) {
      // One life: the fall IS the finish line.
      this.complete();
    }
  }
  hud() {
    return { objective: `DESCEND  //  DEPTH ${this.depth}` };
  }
  medal() { return 'none'; }
}

gameModes.register('sprint', SprintMode, { label: 'SPRINT' });
gameModes.register('tower', TowerMode, { label: 'GAUNTLET TOWER' });
gameModes.register('collect', CollectMode, { label: 'HEIST' });
gameModes.register('gauntlet', GauntletMode, { label: 'GAUNTLET' });

export function createMode(level, ctx) {
  const key = level.def.mode?.type ?? 'sprint';
  const Cls = gameModes.has(key) ? gameModes.get(key) : SprintMode;
  return new Cls(level, ctx);
}
